type WebRtcCodecCapability = RTCRtpCapabilities["codecs"][number];

function parseSdpFmtpLine(sdpFmtpLine?: string): Record<string, string> {
  if (!sdpFmtpLine) {
    return {};
  }

  return sdpFmtpLine
    .split(";")
    .map((part) => part.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, part) => {
      const [rawKey, ...rawValue] = part.split("=");
      const key = rawKey?.trim();
      if (!key) {
        return acc;
      }

      acc[key] = rawValue.join("=").trim();
      return acc;
    }, {});
}

function isMimeType(codec: WebRtcCodecCapability, mimeType: string): boolean {
  return String(codec.mimeType || "").toLowerCase() === mimeType;
}

function getPacketizationMode(codec: WebRtcCodecCapability): string | null {
  const fmtp = parseSdpFmtpLine(codec.sdpFmtpLine);
  return fmtp["packetization-mode"] || null;
}

function getVideoCapabilitiesFromProvider(
  provider:
    | { getCapabilities?: (kind: string) => RTCRtpCapabilities | null }
    | undefined,
): WebRtcCodecCapability[] {
  if (!provider || typeof provider.getCapabilities !== "function") {
    return [];
  }

  const capabilities = provider.getCapabilities("video");
  if (!capabilities || !Array.isArray(capabilities.codecs)) {
    return [];
  }

  return capabilities.codecs;
}

export function getPreferredH264VideoCodecs(): WebRtcCodecCapability[] {
  const receiverCodecs = getVideoCapabilitiesFromProvider(
    typeof RTCRtpReceiver !== "undefined" ? RTCRtpReceiver : undefined,
  );
  const senderCodecs = getVideoCapabilitiesFromProvider(
    typeof RTCRtpSender !== "undefined" ? RTCRtpSender : undefined,
  );
  const resolvedCodecs =
    receiverCodecs.length > 0 ? receiverCodecs : senderCodecs;

  if (resolvedCodecs.length === 0) {
    throw new Error("WebRTC video codec capabilities are unavailable");
  }

  const h264Codecs = resolvedCodecs.filter((codec) => isMimeType(codec, "video/h264"));
  if (h264Codecs.length === 0) {
    throw new Error("H264 is not supported by local WebRTC video capabilities");
  }

  // Chrome commonly exposes both packetization modes, and mode 1 is preferred here.
  const preferredH264 =
    h264Codecs.find((codec) => getPacketizationMode(codec) === "1") ||
    h264Codecs[0];
  const rtxCodecs = resolvedCodecs.filter((codec) => isMimeType(codec, "video/rtx"));

  return [preferredH264, ...rtxCodecs];
}

export function applyPreferredH264VideoCodecPreferences(
  transceivers: RTCRtpTransceiver[],
): void {
  if (!Array.isArray(transceivers) || transceivers.length === 0) {
    return;
  }

  const preferredCodecs = getPreferredH264VideoCodecs();

  transceivers.forEach((transceiver) => {
    if (typeof transceiver.setCodecPreferences !== "function") {
      throw new Error("RTCRtpTransceiver.setCodecPreferences is not supported");
    }

    transceiver.setCodecPreferences(preferredCodecs);
  });
}
